// pages/Agents/Gmail/GmailEmailAgent.js
import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { hasPermission } from "../../../utils/permissionUtils";
import { useLocation, useNavigate, Link, useParams } from 'react-router-dom';

import { Stepper, Step, StepLabel, Button, Box, Typography, Card, CardHeader, CardContent, TextField, Alert } from '@mui/material';
import RouterAgentModal from "../RouterAgentModal";

const steps = ['Connection Details', 'Agent Backstory', 'Task Description'];

const DEFAULT_GMAIL_BACKSTORY = `You are an expert email management assistant with full access to a user's Gmail account.
You are equipped with a suite of tools to read, search, count, and reply to emails.
You meticulously analyze user requests to select the appropriate tool and sequence of tools to fulfill their needs.
You can understand complex email queries and provide detailed, accurate responses.`;

const DEFAULT_GMAIL_DESCRIPTION = `You are an intelligent Gmail assistant. Your primary goal is to help users manage their email efficiently and effectively.

**TOOL SELECTION LOGIC (Follow this strictly):**

1. **FOR EMAIL COUNTING/STATISTICS (Keywords: "how many", "count", "total"):**
   - Use the \`Count Emails\` tool with appropriate filters
   - Examples: "how many emails", "unread count", "emails from specific sender"

2. **FOR EMAIL SEARCHING/LISTING (Keywords: "show me", "list", "find", "search"):**
   - Use the \`List Emails\` tool with appropriate search parameters
   - Examples: "show me emails from John", "list important emails", "find emails about project"

3. **FOR EMAIL CONTENT RETRIEVAL (Keywords: "what does", "content", "body", "read email"):**
   - First use \`List Emails\` to find the specific email
   - Then use \`Get Email Content\` to retrieve the full email body

4. **FOR EMAIL REPLIES (Keywords: "reply", "respond", "send reply"):**
   - First use \`List Emails\` to find the email to reply to
   - Then use \`Reply to Email\` with the email ID and your response

5. **FOR GENERAL EMAIL QUERIES (Keywords: "important", "today", "recent"):**
   - Use \`List Emails\` with date and importance filters
   - Combine multiple tools as needed to provide comprehensive answers

**IMPORTANT RULES:**
- Always provide specific, actionable information
- When listing emails, include sender, subject, and date
- For replies, be professional and contextually appropriate
- If you cannot find specific emails, explain what you searched for
- Always confirm actions like sending replies before executing

**User's Request:** '{user_query}'`;

export default function GmailEmailAgent() {
    const [activeStep, setActiveStep] = useState(0);
    const [isConnected, setIsConnected] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isRouterModalOpen, setRouterModalOpen] = useState(false);
    const [config, setConfig] = useState({ 
        backstory: '', 
        description: '' 
    });
    const location = useLocation();
    const navigate = useNavigate();
    const { chatbotId: paramsChatbotId } = useParams();

    // Permission states
    const [canView, setCanView] = useState(false);
    const [canModify, setCanModify] = useState(false);
    const [checkingPermissions, setCheckingPermissions] = useState(true);

    // Check permissions
    useEffect(() => {
        const checkPermissions = async () => {
            setCheckingPermissions(true);
            try {
                const viewPerm = await hasPermission('agents', 'view');
                const modifyPerm = await hasPermission('agents', 'add|edit');

                setCanView(viewPerm);
                setCanModify(modifyPerm);
            } catch (error) {
                console.error('Error checking permissions:', error);
                setCanView(false);
                setCanModify(false);
            } finally {
                setCheckingPermissions(false);
            }
        };

        checkPermissions();
    }, []);

    useEffect(() => {
        document.title = 'Gmail Email Agent | KlonIT Web Application';
    }, []);

    const fetchAgentAndConnectionStatus = useCallback(async () => {
        setIsLoading(true);
        setError('');
        const params2 = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
        const params = new URLSearchParams(location.search);
        if (params.has('error')) {
            setError(`Authentication failed: ${params.get('error')}. Reason: ${params.get('reason') || 'Unknown'}`);
        }

        try {
            const agentStatusResponse = await axiosInstance.get(`/api/agents/status`, { params: params2 });
            const gmailConfigStatus = agentStatusResponse.data.agents.GMAIL_EMAIL;
            setIsConnected(gmailConfigStatus.is_configured);
            setIsActive(gmailConfigStatus.is_active);

            if (gmailConfigStatus.is_configured) {
                if (params.has('connected')) {
                    setSuccessMessage('Successfully connected to Gmail.');
                }
                const personalityResponse = await axiosInstance.get(`/api/agents/gmail/config`, { params: params2 });
                if (personalityResponse.data.success) {
                    const fetchedConfig = personalityResponse.data.data;
                    setConfig({
                        backstory: (fetchedConfig && fetchedConfig.backstory) || DEFAULT_GMAIL_BACKSTORY,
                        description: (fetchedConfig && fetchedConfig.description) || DEFAULT_GMAIL_DESCRIPTION,
                    });
                }
            } else {
                setConfig({
                    backstory: DEFAULT_GMAIL_BACKSTORY,
                    description: DEFAULT_GMAIL_DESCRIPTION,
                });
            }
        } catch (err) {
            console.error("Error checking Gmail status:", err);
            if (!error) {
                setError(err.response?.data?.message || "Failed to check connection status.");
            }
            setConfig({
                backstory: DEFAULT_GMAIL_BACKSTORY,
                description: DEFAULT_GMAIL_DESCRIPTION,
            });
        } finally {
            setIsLoading(false);
            if (params.has('error') || params.has('connected')) {
                navigate(location.pathname, { replace: true });
            }
        }
    }, [location.search, navigate, error, paramsChatbotId]);

    useEffect(() => {
        if (canView) {
            fetchAgentAndConnectionStatus();
        }
    }, [fetchAgentAndConnectionStatus, canView]);

    const handleToggle = async (newStatus) => {
        if (!canModify) return;

        setIsActive(newStatus);
        setError('');
        setSuccessMessage('');
        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            await axiosInstance.post(`/api/agents/status`, { agent_name: 'GMAIL_EMAIL', is_active: newStatus }, { params });
            setSuccessMessage(`Agent successfully ${newStatus ? 'activated' : 'deactivated'}.`);
        } catch (err) {
            setError('Failed to update agent status.');
            setIsActive(!newStatus);
        }
    };
    
    const handleConnect = async () => {
        setIsProcessing(true);
        setError('');
        setSuccessMessage('');
        
        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            const response = await axiosInstance.get(`/api/gmail-agent/auth/start`, { params });
            if (response.data && response.data.success && response.data.authorization_url) {
                window.location.href = response.data.authorization_url;
            } else {
                setError(response.data.message || "Failed to initiate Gmail connection.");
                setIsProcessing(false);
            }
        } catch (err) {
            setError(err.response?.data?.message || "Could not connect to Gmail. Please try again.");
            setIsProcessing(false);
        }
    };

    const handleDisconnect = async () => {
        setIsProcessing(true);
        setError('');
        setSuccessMessage('');
        
        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            await axiosInstance.post(`/api/gmail-agent/disconnect`, {}, { params });
            setIsConnected(false);
            setIsActive(false); 
            setSuccessMessage('Successfully disconnected from Gmail.');
        } catch (err) {
            setError(err.response?.data?.message || "Failed to disconnect. Please try again.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleConfigChange = (e) => {
        setConfig({ ...config, [e.target.name]: e.target.value });
    };

    const handleSaveConfig = async () => {
        setIsProcessing(true);
        setError('');
        setSuccessMessage('');
        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            const response = await axiosInstance.post(`/api/agents/gmail/config`, config, { params });
            setSuccessMessage(response.data.message);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to save configuration.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleNext = () => setActiveStep((prev) => prev + 1);
    const handleBack = () => setActiveStep((prev) => prev - 1);
    
    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return ( 
                    <Box className="text-center"> 
                        {isConnected ? ( 
                            <> 
                                <p className="text-success">
                                    <i className="fa fa-check-circle"></i> Connected to Gmail.
                                </p> 
                                {canModify && (
                                    <button className="btn btn-lg btn-danger" onClick={handleDisconnect} disabled={isProcessing}> 
                                        {isProcessing ? 'Disconnecting...' : 
                                            <><i className="fa fa-unlink me-2"></i>Disconnect</>
                                        } 
                                    </button>
                                )}
                            </> 
                        ) : ( 
                            <> 
                                <p>Connect your Gmail account to allow the agent to manage emails.</p> 
                                {canModify && (
                                    <button className="btn btn-lg btn-primary" onClick={handleConnect} disabled={isProcessing}> 
                                        {isProcessing ? 'Connecting...' : 
                                            <><i className="fa fa-google me-2"></i>Connect to Gmail</>
                                        } 
                                    </button>
                                )}
                            </> 
                        )} 
                    </Box> 
                );
            case 1:
                return ( 
                    <Box> 
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Define the agent's personality and background. This helps set the context for email management.
                        </Typography> 
                        <TextField 
                            name="backstory" 
                            label="Agent Backstory" 
                            multiline 
                            rows={6} 
                            fullWidth 
                            value={config.backstory || ''} 
                            onChange={handleConfigChange} 
                            placeholder="e.g., You are an expert email management assistant..." 
                            disabled={!canModify}
                        /> 
                    </Box> 
                );
            case 2:
                return ( 
                    <Box> 
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Describe the specific task the agent should perform for email management.
                        </Typography> 
                        <TextField 
                            name="description" 
                            label="Task Description" 
                            multiline 
                            rows={8} 
                            fullWidth 
                            value={config.description || ''} 
                            onChange={handleConfigChange} 
                            placeholder="e.g., Your job is to analyze email requests and take the correct action..." 
                            disabled={!canModify}
                        /> 
                    </Box> 
                );
            default:
                return <Typography>Configuration Complete!</Typography>;
        }
    };

    // Show loading state while checking permissions
    if (checkingPermissions) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                <Typography>Checking permissions...</Typography>
            </Box>
        );
    }

    // Show access denied if no view permission
    if (!canView) {
        return (
            <main className="content">
                <div className="container-fluid p-0">
                    <Alert severity="error" sx={{ mt: 6 }}>
                        <strong>Access Denied</strong>
                        <br />
                        You don't have permission to view Gmail Email Agent. Please contact your administrator.
                    </Alert>
                </div>
            </main>
        );
    }

    if (isLoading) return (
        <div className="container-fluid p-0 text-center mt-5">
            <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
        </div>
    );
    
    return (
        <main className="content">
            <div className="container-fluid p-0">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h1 className="h3 d-inline align-middle"><strong>Gmail Email Agent</strong></h1>
                    <Link to="/agents" className="btn btn-secondary">
                        <i className="fa fa-arrow-left me-2"></i>Back to Agents
                    </Link>
                </div>

                {successMessage && <div className="alert alert-success">{successMessage}</div>}
                {error && <div className="alert alert-danger">{error}</div>}
                {!canModify && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        You have view-only access. Contact your administrator to make changes.
                    </Alert>
                )}

                <Card>
                    <CardHeader
                        title="Agent Status"
                        action={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Button variant="outlined" size="small" onClick={() => setRouterModalOpen(true)} disabled={!canModify}>
                                    Edit Router Agent
                                </Button>
                                <div className="form-check form-switch mt-2">
                                    <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        role="switch" 
                                        checked={isActive} 
                                        onChange={(e) => handleToggle(e.target.checked)} 
                                        disabled={!isConnected || !canModify} 
                                        id="gmail-agent-switch" 
                                    />
                                    <label className="form-check-label" htmlFor="gmail-agent-switch">
                                        {isActive ? 'Active' : 'Inactive'}
                                    </label>
                                </div>
                            </Box>
                        }
                    />
                    <CardContent>
                        <Typography variant="body2" color="text.secondary">
                            {isActive ? 
                                "The Gmail Email agent is active and ready to handle email requests." : 
                                "The agent is inactive. Activate it to allow email management."
                            }
                        </Typography>
                    </CardContent>
                </Card>

                <Card sx={{ mt: 3 }}>
                    <CardContent>
                        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                        
                        {renderStepContent(activeStep)}

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                            <Button disabled={activeStep === 0} onClick={handleBack}>
                                Back
                            </Button>
                            {activeStep < steps.length - 1 ? (
                                <Button variant="contained" onClick={handleNext} disabled={!isConnected}>
                                    Next
                                </Button>
                            ) : (
                                canModify && (
                                    <Button 
                                        variant="contained" 
                                        color="primary" 
                                        onClick={handleSaveConfig} 
                                        disabled={isProcessing || !isConnected}
                                    >
                                        {isProcessing ? 'Saving...' : 'Save Configuration'}
                                    </Button>
                                )
                            )}
                        </Box>
                    </CardContent>
                </Card>
                <RouterAgentModal open={isRouterModalOpen} onClose={() => setRouterModalOpen(false)} />
            </div>
        </main>
    );
}